'use client';

import { useTranslations } from 'next-intl';
import { useCallback, useMemo, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  FieldTriggerChevron,
  fieldTriggerClasses,
  FieldTriggerLabel,
} from '@/components/ui/field-trigger';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useBoolean } from '@/hooks/utils';
import { cn } from '@/lib/utils';
import type { TranslateFunction } from '@/types/general';
import { getLanguageOptions } from '@/utils/language-helpers';

import FlagByLanguage from './FlagByLanguage';

// Stable default arrays to prevent infinite render loops
const DEFAULT_VALUE: string[] = [];
const DEFAULT_AVAILABLE_LANGUAGES: string[] = [];

export type LanguageMultiSelectorProps = {
  name?: string;
  title?: string;
  description?: string;
  placeholder?: string;
  value?: string[];
  onChange?: (value: string[]) => void;
  availableLanguages?: string[];
  allLanguages?: string[];
  loading?: boolean;
  disabled?: boolean;
  className?: string;
};

type LanguageItem = {
  code: string;
  name: string;
};

export function LanguageMultiSelector({
  name,
  title,
  description,
  placeholder,
  value = DEFAULT_VALUE,
  onChange,
  availableLanguages = DEFAULT_AVAILABLE_LANGUAGES,
  allLanguages,
  loading = false,
  disabled = false,
  className,
}: LanguageMultiSelectorProps) {
  const translate = useTranslations();
  const { value: isOpen, setValue: setIsOpen } = useBoolean(false);
  const [search, setSearch] = useState<string>('');

  // Determine which languages to show
  const languagesToShow = useMemo(() => {
    if (allLanguages) {
      return allLanguages;
    }
    const selectedUnavailable = value.filter(lang => availableLanguages.includes(lang) === false);
    return [...new Set([...availableLanguages, ...selectedUnavailable])];
  }, [allLanguages, availableLanguages, value]);

  const allOptions: LanguageItem[] = useMemo(() => {
    return getLanguageOptions(translate as TranslateFunction, languagesToShow);
  }, [languagesToShow, translate]);

  const filteredOptions = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (query === '') {
      return allOptions;
    }
    return allOptions.filter(option =>
      option.name.toLowerCase().includes(query) === true
      || option.code.toLowerCase().includes(query) === true,
    );
  }, [allOptions, search]);

  const optionByCode = useMemo(() => {
    const map = new Map<string, LanguageItem>();
    allOptions.forEach((option) => {
      map.set(option.code, option);
    });
    return map;
  }, [allOptions]);

  const toggle = useCallback(
    (code: string) => {
      if (onChange === undefined) {
        return;
      }
      if (value.includes(code) === true) {
        onChange(value.filter(item => item !== code));
        return;
      }
      onChange([...value, code]);
    },
    [onChange, value],
  );

  const removeChip = useCallback(
    (code: string) => {
      if (onChange === undefined) {
        return;
      }
      onChange(value.filter(item => item !== code));
    },
    [onChange, value],
  );

  const triggerId = name !== undefined && name !== '' ? name : undefined;
  const hasTitle = title !== undefined && title !== '';
  const hasValue = value.length > 0;
  const placeholderText = placeholder ?? translate('course.languages.selectLanguages');

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <div className="flex w-full flex-col gap-1.5">
          {hasTitle === true && (
            <FieldTriggerLabel htmlFor={triggerId}>{title}</FieldTriggerLabel>
          )}
          <PopoverTrigger asChild>
            <button
              type="button"
              id={triggerId}
              disabled={disabled === true || loading === true}
              data-state={isOpen === true ? 'open' : 'closed'}
              // No-chip state: stay at the canonical h-8 trigger height so the
              // selector lines up with sibling ComboBox / Select fields on the
              // same form row. Once chips appear, grow to fit (min-h-8 baseline,
              // max-h-32 cap matches ComboBox multi-select) and switch to
              // top-aligned content so the chevron tracks the first chip row.
              className={fieldTriggerClasses(
                {},
                cn(
                  'text-left',
                  hasValue === true && 'h-auto min-h-8 max-h-32 overflow-y-auto items-start py-1',
                ),
              )}
            >
              <span
                className={cn(
                  'flex min-w-0 flex-1 flex-wrap gap-1',
                  hasValue === true ? 'items-start' : 'items-center',
                )}
              >
                {hasValue === false && hasTitle === false && (
                  <span className="text-on-surface-variant/60">{placeholderText}</span>
                )}
                {value.map((code) => {
                  const option = optionByCode.get(code);
                  const labelText = option !== undefined ? option.name : code;
                  return (
                    <Badge
                      key={code}
                      variant="primary"
                      size="sm"
                      className="gap-1.5 pr-1 normal-case"
                    >
                      <FlagByLanguage code={code} showLabel={false} size="md" />
                      <span className="max-w-[120px] truncate">{labelText}</span>
                      <button
                        type="button"
                        aria-label={translate('common.remove')}
                        onClick={(event) => {
                          event.stopPropagation();
                          removeChip(code);
                        }}
                        className="inline-flex size-3.5 shrink-0 cursor-pointer items-center justify-center rounded-sm p-0 opacity-70 hover:opacity-100 text-on-primary-container hover:text-error"
                      >
                        <Icon icon="lucide:x" size={12} />
                      </button>
                    </Badge>
                  );
                })}
              </span>
              <span className={cn('flex shrink-0 items-center', hasValue === true && 'pt-1')}>
                <FieldTriggerChevron isOpen={isOpen} />
              </span>
            </button>
          </PopoverTrigger>
        </div>

        <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] p-2">
          <div className="flex flex-col gap-2">
            <Input
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
              }}
              placeholder={translate('editingLanguage.search')}
              startIcon="lucide:search"
              inputSize="sm"
            />

            <div className="max-h-[320px] overflow-y-auto">
              <ul className="flex flex-col">
                {filteredOptions.length === 0 && (
                  <li className="px-2 py-3 text-center text-xs text-on-surface-variant">
                    {translate('common.notAvailable')}
                  </li>
                )}
                {filteredOptions.map((option) => {
                  const isSelected = value.includes(option.code);
                  return (
                    <li key={option.code}>
                      <button
                        type="button"
                        onClick={() => {
                          toggle(option.code);
                        }}
                        aria-pressed={isSelected}
                        className={cn(
                          'flex h-9 w-full items-center gap-2.5 rounded-sm px-2.5 text-left type-body-m text-foreground transition-colors duration-[var(--duration-short-3)] hover:bg-foreground/8 focus-visible:bg-foreground/8 focus-visible:outline-none motion-reduce:transition-none',
                        )}
                      >
                        <Checkbox
                          checked={isSelected}
                          tabIndex={-1}
                          aria-hidden
                          className="pointer-events-none"
                        />
                        <FlagByLanguage code={option.code} showLabel={false} size="md" />
                        <span className="flex-1 truncate">{option.name}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {description !== undefined && description !== '' && (
        <p className="type-body-s text-on-surface-variant">{description}</p>
      )}
    </div>
  );
}

export default LanguageMultiSelector;
